/**
 * MATERIALITY (R8.1, ADR-0015) — a person's judgment about the engagement:
 * overall, performance, clearly trivial, and the basis in their own words.
 *
 * Presentation only, here and everywhere: it decides how a difference is
 * stated and how work is ranked, never what Leveri concludes. Every figure on
 * this screen is the server's text; the amounts a person types are sent as
 * typed and converted on the server. Whether this viewer may set it is the
 * server's answer (`viewerMaySet`), never inferred from a title.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { MaterialityResponse, type Engagement } from "@leveri/contracts";
import { Button, Definitions, Field, Notice, Reading, Section, SectionHead, TextField } from "../file/index.js";
import { call, failureMessage } from "../api.js";

export function MaterialitySection({ engagement }: { engagement: Engagement }) {
  const [state, setState] = useState<MaterialityResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailure, setSaveFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await call("GET", `/api/engagements/${engagement.id}/materiality`, MaterialityResponse));
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, [engagement.id]);
  useEffect(() => { void load(); }, [load]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const values = new FormData(event.currentTarget);
    const read = (name: string) => String(values.get(name) ?? "").trim();
    setSaving(true);
    setSaveFailure(null);
    try {
      setState(await call("POST", `/api/engagements/${engagement.id}/materiality`, MaterialityResponse, {
        overall: read("overall"), performance: read("performance"), clearlyTrivial: read("clearlyTrivial"), basis: read("basis"),
      }));
    } catch (error) {
      setSaveFailure(failureMessage(error));
    } finally {
      setSaving(false);
    }
  }

  const m = state?.materiality ?? null;
  return (
    <Section>
      <SectionHead>Materiality</SectionHead>
      {failure && <Notice>{failure}</Notice>}
      {!state && !failure && <Reading>Reading materiality…</Reading>}
      {state && !m && (
        <p className="text-sm text-stone" data-testid="materiality-unset">
          No materiality is set. Differences on this engagement are stated without it.
        </p>
      )}
      {m && (
        <Definitions
          items={[
            { key: "overall", term: "Overall materiality", value: m.overallText },
            { key: "performance", term: "Performance materiality", value: m.performanceText },
            { key: "trivial", term: "Clearly trivial", value: m.clearlyTrivialText },
            { key: "basis", term: "Basis", value: `${m.basis} — set by ${m.setByName}` },
          ]}
        />
      )}
      {state?.viewerMaySet && (
        <form onSubmit={save} className="mt-5 grid grid-cols-1 items-end gap-4 md:grid-cols-3" data-testid="materiality-form">
          <TextField name="overall" label={`Overall (${engagement.currency})`} inputMode="decimal" required disabled={saving} />
          <TextField name="performance" label={`Performance (${engagement.currency})`} inputMode="decimal" required disabled={saving} />
          <TextField name="clearlyTrivial" label={`Clearly trivial (${engagement.currency})`} inputMode="decimal" required disabled={saving} />
          <div className="md:col-span-3">
            <Field label="Basis" hint="How you arrived at it. Filed with it; setting it again keeps the earlier one in the record.">
              <textarea name="basis" rows={2} required disabled={saving}
                className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" />
            </Field>
          </div>
          <div className="md:col-span-3">
            <Button type="submit" disabled={saving}>{m ? "Set materiality again" : "Set materiality"}</Button>
          </div>
        </form>
      )}
      {saving && <div className="mt-2"><Reading>Recording materiality…</Reading></div>}
      {saveFailure && <div className="mt-2"><Notice>{saveFailure}</Notice></div>}
    </Section>
  );
}
